import type {
  AnnotateCurrentUser,
  ConsoleEntry,
  NetworkEntry,
  ProjectMember,
  TestingItem,
  TestingItemComment,
  TestingItemCoordinates,
  TestingItemPriority,
  TestingItemSeverity,
  TestingItemStatus,
} from './types'

interface ApiOptions {
  getAccessToken?: () => string | null
}

interface MutationFallback {
  projectId: string
  itemId?: string
  url?: string
  domain?: string
  missingStatus?: TestingItemStatus
}

export interface ListItemsParams {
  projectId: string
  status?: string
  assignedTo?: 'me' | 'unassigned' | 'all' | number
  createdBy?: 'me' | number
  url?: string
  domain?: string
  page?: number
  limit?: number
}

export interface CreateTestingItemPayload {
  projectId: string
  title: string
  url?: string
  domain?: string
  description?: string
  priority?: TestingItemPriority
  severity?: TestingItemSeverity
  screenshot?: string
  element?: string
  selectorPath?: string
  coordinates?: TestingItemCoordinates
  consoleLog?: ConsoleEntry[]
  userAgent?: string
  browser?: string
  device?: string
  os?: string
  viewportWidth?: number
  viewportHeight?: number
  networkRequests?: NetworkEntry[]
  resolution?: string
  assignedTo?: number | null
}

export interface UpdateTestingItemPayload {
  projectId: string
  title?: string
  description?: string | null
  priority?: TestingItemPriority
  severity?: TestingItemSeverity
  status?: TestingItemStatus
  assignedTo?: number | null
  prLink?: string | null
}

export class InstrucktAuthError extends Error {
  readonly status = 401

  constructor(message = `visual-qa: ${authErrorMessage()}`) {
    super(message)
    this.name = 'InstrucktAuthError'
  }
}

export function isAuthError(error: unknown): error is InstrucktAuthError {
  return error instanceof InstrucktAuthError
}

function headers(token?: string, includeJson = false): Record<string, string> {
  const h: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  }
  if (includeJson) h['Content-Type'] = 'application/json'
  if (token) h.Authorization = `Bearer ${token}`
  return h
}

function authErrorMessage(): string {
  return 'Your session expired or you are not authorized. Please log in again and try again.'
}

function parseJsonArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[]
  if (typeof value !== 'string' || !value.trim()) return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed as T[] : []
  } catch {
    return []
  }
}

function parseNullableId(value: unknown): number | null {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function requestDomain(): string {
  return window.location.hostname
}

function parseCoordinates(value: unknown): TestingItemCoordinates | undefined {
  if (!value) return undefined
  if (typeof value === 'string' && value.includes(',')) {
    const [xRaw, yRaw] = value.split(',').map(part => part.trim())
    const x = Number(xRaw)
    const y = Number(yRaw)
    if (Number.isFinite(x) && Number.isFinite(y)) return { x, y }
  }
  const raw = typeof value === 'string' ? (() => {
    try {
      return JSON.parse(value) as Record<string, unknown>
    } catch {
      return null
    }
  })() : value
  if (!raw || typeof raw !== 'object') return undefined
  const x = Number((raw as Record<string, unknown>).x)
  const y = Number((raw as Record<string, unknown>).y)
  if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined
  const pagePath = (raw as Record<string, unknown>).page_path ?? (raw as Record<string, unknown>).pagePath
  const anchorXRatio = Number(
    (raw as Record<string, unknown>).ax ??
    (raw as Record<string, unknown>).anchor_x_ratio ??
    (raw as Record<string, unknown>).anchorXRatio
  )
  const anchorYRatio = Number(
    (raw as Record<string, unknown>).ay ??
    (raw as Record<string, unknown>).anchor_y_ratio ??
    (raw as Record<string, unknown>).anchorYRatio
  )
  return {
    x,
    y,
    pagePath: typeof pagePath === 'string' ? pagePath : undefined,
    anchorXRatio: Number.isFinite(anchorXRatio) ? anchorXRatio : undefined,
    anchorYRatio: Number.isFinite(anchorYRatio) ? anchorYRatio : undefined,
  }
}

function normalizeComments(value: unknown): TestingItemComment[] {
  return parseJsonArray<Record<string, unknown>>(value).map(comment => ({
    id: String(comment.id ?? ''),
    staffid: Number(comment.staffid ?? 0),
    name: String(comment.name ?? ''),
    message: String(comment.message ?? ''),
    createdAt: String(comment.created_at ?? comment.createdAt ?? ''),
  })).filter(comment => comment.id && comment.message)
}

function normalizeMember(raw: Record<string, unknown>): ProjectMember {
  const staffid = parseNullableId(raw.staffid ?? raw.id)
  return {
    staffid: staffid ?? 0,
    firstname: typeof raw.firstname === 'string' ? raw.firstname : null,
    lastname: typeof raw.lastname === 'string' ? raw.lastname : null,
    email: typeof raw.email === 'string' ? raw.email : null,
    name: typeof raw.name === 'string'
      ? raw.name
      : [raw.firstname, raw.lastname].filter(part => typeof part === 'string' && part).join(' ') || null,
  }
}

function normalizeCurrentUser(raw: Record<string, unknown>): AnnotateCurrentUser {
  return {
    id: Number(raw.id ?? 0),
    name: String(raw.name ?? ''),
    firstname: typeof raw.firstname === 'string' ? raw.firstname : null,
    lastname: typeof raw.lastname === 'string' ? raw.lastname : null,
    email: typeof raw.email === 'string' ? raw.email : null,
    phone: typeof raw.phone === 'string' ? raw.phone : null,
    profileImage: typeof raw.profile_image === 'string' ? raw.profile_image : null,
    roleId: raw.role_id == null ? null : Number(raw.role_id),
    roleName: typeof raw.role_name === 'string' ? raw.role_name : null,
    userFunction: typeof raw.function === 'string' ? raw.function : null,
    isAdmin: Boolean(raw.is_admin),
    active: raw.active !== false,
  }
}

function normalizeItem(raw: Record<string, unknown>, forceFullDetails = false): TestingItem {
  const hasFullDetails = forceFullDetails || String(raw._full) === 'true'

  return {
    id: String(raw.id ?? ''),
    projectId: String(raw.project_id ?? ''),
    title: String(raw.title ?? ''),
    url: typeof raw.url === 'string' ? raw.url : '',
    status: String(raw.status ?? 'open') as TestingItemStatus,
    priority: String(raw.priority ?? 'medium') as TestingItemPriority,
    severity: String(raw.severity ?? 'minor') as TestingItemSeverity,
    assignedTo: parseNullableId(raw.assigned_to),
    createdBy: parseNullableId(raw.created_by),
    prLink: typeof raw.pr_link === 'string' ? raw.pr_link : null,
    resolution: typeof raw.resolution === 'string' ? raw.resolution : null,
    createdAt: String(raw.created_at ?? ''),
    updatedAt: String(raw.updated_at ?? ''),
    comments: normalizeComments(raw.comments),
    description: typeof raw.description === 'string' ? raw.description : undefined,
    domain: typeof raw.domain === 'string' ? raw.domain : undefined,
    screenshot: typeof raw.screenshot === 'string' ? raw.screenshot : undefined,
    element: typeof raw.element === 'string' ? raw.element : undefined,
    selectorPath: typeof raw.selector_path === 'string' ? raw.selector_path : undefined,
    coordinates: parseCoordinates(raw.coordinates),
    consoleLog: parseJsonArray<ConsoleEntry>(raw.console_log),
    userAgent: typeof raw.user_agent === 'string' ? raw.user_agent : undefined,
    browser: typeof raw.browser === 'string' ? raw.browser : undefined,
    device: typeof raw.device === 'string' ? raw.device : undefined,
    os: typeof raw.os === 'string' ? raw.os : undefined,
    viewportWidth: typeof raw.viewport_width === 'number' ? raw.viewport_width : undefined,
    viewportHeight: typeof raw.viewport_height === 'number' ? raw.viewport_height : undefined,
    networkRequests: parseJsonArray<NetworkEntry>(raw.network_requests),
    assignedName: typeof (raw.assigned_user as Record<string, unknown> | undefined)?.name === 'string'
      ? String((raw.assigned_user as Record<string, unknown>).name)
      : undefined,
    createdName: typeof (raw.created_by_user as Record<string, unknown> | undefined)?.name === 'string'
      ? String((raw.created_by_user as Record<string, unknown>).name)
      : undefined,
    assignedFirst: typeof raw.assigned_first === 'string' ? raw.assigned_first : undefined,
    assignedLast: typeof raw.assigned_last === 'string' ? raw.assigned_last : undefined,
    createdFirst: typeof raw.created_first === 'string' ? raw.created_first : undefined,
    createdLast: typeof raw.created_last === 'string' ? raw.created_last : undefined,
    hasFullDetails,
  }
}

function serializeCreatePayload(data: CreateTestingItemPayload): Record<string, unknown> {
  return {
    project_id: Number(data.projectId),
    title: data.title,
    url: data.url,
    domain: data.domain,
    description: data.description,
    priority: data.priority,
    severity: data.severity,
    screenshot: data.screenshot,
    element: data.element,
    selector_path: data.selectorPath,
    coordinates: data.coordinates ? JSON.stringify({
      x: Math.round(data.coordinates.x),
      y: Math.round(data.coordinates.y),
      ax: data.coordinates.anchorXRatio != null ? Math.round(data.coordinates.anchorXRatio * 100) / 100 : undefined,
      ay: data.coordinates.anchorYRatio != null ? Math.round(data.coordinates.anchorYRatio * 100) / 100 : undefined,
    }) : undefined,
    console_log: data.consoleLog ? JSON.stringify(data.consoleLog) : undefined,
    user_agent: data.userAgent,
    browser: data.browser,
    device: data.device,
    os: data.os,
    viewport_width: data.viewportWidth,
    viewport_height: data.viewportHeight,
    network_requests: data.networkRequests ? JSON.stringify(data.networkRequests) : undefined,
    resolution: data.resolution,
    assigned_to: data.assignedTo ?? null,
  }
}

export class InstrucktApi {
  constructor(
    private readonly endpoint: string,
    private readonly options: ApiOptions = {},
  ) {}

  private authHeaders(includeJson = false): Record<string, string> {
    return headers(this.options.getAccessToken?.() ?? undefined, includeJson)
  }

  private itemUrl(path = ''): string {
    return `${this.endpoint.replace(/\/$/, '')}/items${path}`
  }

  private meUrl(): string {
    return `${this.endpoint.replace(/\/$/, '')}/me`
  }

  private domainsUrl(projectId: string): string {
    const url = new URL(`${this.endpoint.replace(/\/$/, '')}/domains`, window.location.href)
    url.searchParams.set('project_id', projectId)
    return url.toString()
  }

  private membersUrl(projectId: string): string {
    const url = new URL(`${this.endpoint.replace(/\/$/, '')}/members`, window.location.href)
    url.searchParams.set('project_id', projectId)
    return url.toString()
  }

  private ensureOk(res: Response, action: string): void {
    if (res.status === 401) throw new InstrucktAuthError()
    if (!res.ok) throw new Error(`visual-qa: failed to ${action} (${res.status})`)
  }

  private async createError(res: Response, action: string): Promise<Error> {
    if (res.status === 401) return new InstrucktAuthError()
    try {
      const raw = await res.json() as Record<string, unknown>
      const message = typeof raw.error === 'string'
        ? raw.error
        : typeof raw.message === 'string'
          ? raw.message
          : null
      if (message) return new Error(`visual-qa: ${message}`)
    } catch {}
    return new Error(`visual-qa: failed to ${action} (${res.status})`)
  }

  private async readItemResponse(res: Response, action: string, fullDetails = false): Promise<TestingItem> {
    this.ensureOk(res, action)
    const raw = await res.json() as Record<string, unknown>
    const item = raw.item
    return normalizeItem((item && typeof item === 'object' ? item : raw) as Record<string, unknown>, fullDetails)
  }

  private looksLikeItem(raw: Record<string, unknown>): boolean {
    return typeof raw.id !== 'undefined' && typeof raw.project_id !== 'undefined' && typeof raw.title === 'string'
  }

  private syntheticItem(fallback: Required<Pick<MutationFallback, 'itemId' | 'projectId' | 'missingStatus'>>): TestingItem {
    const now = new Date().toISOString()
    return {
      id: fallback.itemId,
      projectId: fallback.projectId,
      title: '',
      url: '',
      status: fallback.missingStatus,
      priority: 'medium',
      severity: 'minor',
      assignedTo: null,
      createdBy: null,
      prLink: null,
      resolution: null,
      createdAt: now,
      updatedAt: now,
      comments: [],
    }
  }

  private async refetchItem(action: string, fallback: MutationFallback): Promise<TestingItem> {
    if (!fallback.itemId) throw new Error('visual-qa: mutation response missing item id')

    try {
      return await this.getItemDetail(fallback.projectId, fallback.itemId)
    } catch {}

    const first = await this.listItems({
      projectId: fallback.projectId,
      url: fallback.url,
      domain: fallback.domain,
      page: 1,
      limit: 100,
    })
    const firstMatch = first.items.find(item => item.id === fallback.itemId)
    if (firstMatch) return firstMatch

    const totalPages = Math.max(1, Math.ceil(first.total / first.limit))
    for (let page = 2; page <= totalPages; page++) {
      const next = await this.listItems({
        projectId: fallback.projectId,
        url: fallback.url,
        domain: fallback.domain,
        page,
        limit: first.limit,
      })
      const match = next.items.find(item => item.id === fallback.itemId)
      if (match) return match
    }

    if (fallback.missingStatus) {
      return this.syntheticItem({
        itemId: fallback.itemId,
        projectId: fallback.projectId,
        missingStatus: fallback.missingStatus,
      })
    }

    throw new Error(`visual-qa: failed to ${action} (item ${fallback.itemId} not found after revalidation)`)
  }

  private async readMutationItemResponse(res: Response, action: string, fallback: MutationFallback): Promise<TestingItem> {
    this.ensureOk(res, action)
    const raw = await res.json() as Record<string, unknown>
    const nested = raw.item
    if (nested && typeof nested === 'object') return normalizeItem(nested as Record<string, unknown>, true)
    if (this.looksLikeItem(raw)) return normalizeItem(raw, true)

    const id = raw.id == null ? fallback.itemId : String(raw.id)
    return this.refetchItem(action, {
      ...fallback,
      itemId: id,
    })
  }

  async listItems(params: ListItemsParams): Promise<{ items: TestingItem[]; total: number; page: number; limit: number }> {
    const url = new URL(this.itemUrl(), window.location.href)
    url.searchParams.set('project_id', params.projectId)
    if (params.status) url.searchParams.set('status', params.status)
    if (params.assignedTo !== undefined) url.searchParams.set('assigned_to', String(params.assignedTo))
    if (params.createdBy !== undefined) url.searchParams.set('created_by', String(params.createdBy))
    if (params.url) url.searchParams.set('url', params.url)
    if (params.domain) url.searchParams.set('domain', params.domain)
    else url.searchParams.set('domain', requestDomain())
    if (params.page) url.searchParams.set('page', String(params.page))
    if (params.limit) url.searchParams.set('limit', String(params.limit))

    const res = await fetch(url.toString(), { headers: this.authHeaders(), cache: 'no-store' })
    this.ensureOk(res, 'load items')
    const raw = await res.json() as Record<string, unknown>
    const items = Array.isArray(raw.items) ? raw.items : []
    return {
      items: items.map(item => normalizeItem(item as Record<string, unknown>)),
      total: Number(raw.total ?? items.length),
      page: Number(raw.page ?? 1),
      limit: Number((raw.limit ?? items.length) || 20),
    }
  }

  async listMembers(projectId: string): Promise<ProjectMember[]> {
    const res = await fetch(this.membersUrl(projectId), { headers: this.authHeaders(), cache: 'no-store' })
    this.ensureOk(res, 'load members')
    const raw = await res.json()
    const members = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as Record<string, unknown>).members)
        ? (raw as Record<string, unknown>).members as unknown[]
        : []
    return members
      .map(member => normalizeMember(member as Record<string, unknown>))
      .filter(member => member.staffid > 0)
  }

  async getMe(): Promise<AnnotateCurrentUser> {
    const res = await fetch(this.meUrl(), { headers: this.authHeaders(), cache: 'no-store' })
    this.ensureOk(res, 'load current user')
    return normalizeCurrentUser(await res.json() as Record<string, unknown>)
  }

  async getItemDetail(projectId: string, itemId: string): Promise<TestingItem> {
    const url = new URL(this.itemUrl(`/${itemId}`), window.location.href)
    url.searchParams.set('project_id', projectId)
    const res = await fetch(url.toString(), { headers: this.authHeaders(), cache: 'no-store' })
    return this.readItemResponse(res, 'load item detail', true)
  }

  async listDomains(projectId: string): Promise<string[]> {
    const res = await fetch(this.domainsUrl(projectId), { headers: this.authHeaders(), cache: 'no-store' })
    this.ensureOk(res, 'load domains')
    const raw = await res.json() as Record<string, unknown>
    return Array.isArray(raw.domains) ? raw.domains.filter((value): value is string => typeof value === 'string') : []
  }

  async createItem(data: CreateTestingItemPayload): Promise<TestingItem> {
    const res = await fetch(this.itemUrl(), {
      method: 'POST',
      headers: this.authHeaders(true),
      body: JSON.stringify(serializeCreatePayload(data)),
    })
    if (!res.ok) throw await this.createError(res, 'create item')
    return this.readMutationItemResponse(res, 'create item', {
      projectId: data.projectId,
      url: data.url,
      domain: data.domain,
    })
  }

  async addComment(itemId: string, data: { projectId: string; message: string }): Promise<TestingItem> {
    const res = await fetch(this.itemUrl(`/${itemId}`), {
      method: 'PATCH',
      headers: this.authHeaders(true),
      body: JSON.stringify({
        project_id: Number(data.projectId),
        action: 'add',
        message: data.message,
      }),
    })
    return this.readMutationItemResponse(res, 'add comment', {
      projectId: data.projectId,
      itemId,
      domain: requestDomain(),
    })
  }

  async editComment(itemId: string, data: { projectId: string; commentId: string; message: string }): Promise<TestingItem> {
    const res = await fetch(this.itemUrl(`/${itemId}`), {
      method: 'PATCH',
      headers: this.authHeaders(true),
      body: JSON.stringify({
        project_id: Number(data.projectId),
        action: 'edit',
        comment_id: data.commentId,
        message: data.message,
      }),
    })
    return this.readMutationItemResponse(res, 'edit comment', {
      projectId: data.projectId,
      itemId,
      domain: requestDomain(),
    })
  }

  async updateItem(itemId: string, data: UpdateTestingItemPayload): Promise<TestingItem> {
    const payload: Record<string, unknown> = {
      project_id: Number(data.projectId),
    }
if (data.title !== undefined) payload.title = data.title
  if ('description' in data) payload.description = data.description ?? null
    if (data.priority !== undefined) payload.priority = data.priority
    if (data.severity !== undefined) payload.severity = data.severity
    if (data.status !== undefined) payload.status = data.status
    if ('assignedTo' in data) payload.assigned_to = data.assignedTo ?? null
    if ('prLink' in data) payload.pr_link = data.prLink ?? null

    const res = await fetch(this.itemUrl(`/${itemId}`), {
      method: 'PUT',
      headers: this.authHeaders(true),
      body: JSON.stringify(payload),
    })
    return this.readMutationItemResponse(res, 'update item', {
      projectId: data.projectId,
      itemId,
      domain: requestDomain(),
      missingStatus: data.status === 'closed' ? 'closed' : undefined,
    })
  }

  async resolveItem(itemId: string, data: { projectId: string; resolution: string }): Promise<TestingItem> {
    const res = await fetch(this.itemUrl(`/${itemId}/resolve`), {
      method: 'POST',
      headers: this.authHeaders(true),
      body: JSON.stringify({
        project_id: Number(data.projectId),
        resolution: data.resolution,
      }),
    })
    return this.readMutationItemResponse(res, 'resolve item', {
      projectId: data.projectId,
      itemId,
      domain: requestDomain(),
      missingStatus: 'resolved',
    })
  }
}
